import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTrackedTimeout } from '../../hooks/useTrackedTimeout';
import {
  useWebAnalyticsReport,
  useGA4Properties,
  useEngagementBySource,
  useEngagementTrend,
} from '../../hooks/useWebAnalytics';
import ReportFilters from '../../components/domain/ReportFilters';
import { Spinner } from '../../components/common/Spinner';
import type {
  PropertyAnalytics,
  WebAnalyticsFilters,
  WebAnalyticsReport,
  CountryBreakdown,
  DeviceBreakdown,
  DuplicateTagSite,
  StreamWarning,
} from '../../services/api/webAnalytics';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const PRODUCT_COLORS: Record<string, string> = {
  PriceMonk: '#4CB3AC',
  MatchMonk: '#F17B86',
  TidyMonk: '#4849B8',
  ScrapeMonk: '#6C60B6',
  MonkSuite: '#FC8332',
  Wistery: '#0EA5E9',
};

// Deterministic fallback palette for products with no explicit entry above,
// so a newly registered product (added via /admin/web-analytics-sites, no
// frontend deploy) still gets a stable, legible colour instead of a generic
// grey. The same product name always hashes to the same palette slot.
const FALLBACK_COLOR_PALETTE = ['#EC4899', '#14B8A6', '#F59E0B', '#8B5CF6', '#84CC16', '#06B6D4'];

function hashProductName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function getProductColor(name: string): string {
  const explicit = PRODUCT_COLORS[name];
  if (explicit) return explicit;
  const index = hashProductName(name) % FALLBACK_COLOR_PALETTE.length;
  // Modulo against the palette's own length always yields a valid index, so
  // the fallback below never actually triggers — it only satisfies the
  // compiler's noUncheckedIndexedAccess check.
  return FALLBACK_COLOR_PALETTE[index] ?? FALLBACK_COLOR_PALETTE[0]!;
}

const CHANNEL_COLORS: Record<string, string> = {
  'Organic Search': '#10B981',
  Direct: '#3B82F6',
  Referral: '#FC8332',
  'Organic Social': '#8B5CF6',
  'Paid Search': '#EF4444',
  Email: '#F59E0B',
  Display: '#EC4899',
  Unassigned: '#9CA3AF',
};

const DEVICE_COLORS: Record<string, string> = {
  desktop: '#3B82F6',
  mobile: '#FC8332',
  tablet: '#10B981',
};

function formatDate(dateStr: string): string {
  if (dateStr.length === 8) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  }
  return dateStr;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  // F2-011: track the onBlur dismiss timer so an unmount before 150ms
  // doesn't fire setState on a detached component. Mirror of cycle-1
  // F1-009/010 sweep.
  const { schedule } = useTrackedTimeout();

  return (
    <span className="relative inline-block ml-1.5">
      <button
        onClick={() => setOpen(!open)}
        onBlur={() => schedule(() => setOpen(false), 150)}
        className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 inline-flex items-center justify-center text-xs font-bold transition-colors"
        title="Help"
      >
        ?
      </button>
      {open && (
        <div className="absolute z-50 left-1/2 -translate-x-1/2 mt-2 w-72 bg-gray-800 text-white text-xs rounded-lg p-3 shadow-lg leading-relaxed">
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-800 rotate-45" />
          <span className="relative">{text}</span>
        </div>
      )}
    </span>
  );
}

function SectionTitle({ title, tooltip }: { title: string; tooltip: string }) {
  return (
    <h2 className="text-lg font-semibold text-gray-900 mt-8 flex items-center">
      {title}
      <InfoTooltip text={tooltip} />
    </h2>
  );
}

function ChartTitle({ title, tooltip }: { title: string; tooltip: string }) {
  return (
    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
      {title}
      <InfoTooltip text={tooltip} />
    </h3>
  );
}

function KpiCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-semibold mt-1" style={{ color: color ?? '#1D1D25' }}>
        {value}
      </p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// ===== Overview Section =====

function OverviewSection({ report }: { report: WebAnalyticsReport }) {
  const combinedDaily = (() => {
    const dateMap = new Map<string, Record<string, string | number>>();
    for (const prop of report.properties) {
      for (const d of prop.daily) {
        const date = formatDate(d.date);
        const existing = dateMap.get(date) ?? { date };
        existing[prop.metrics.propertyName] = d.sessions;
        dateMap.set(date, existing);
      }
    }
    return Array.from(dateMap.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );
  })();

  const pieData = report.properties.map((p) => ({
    name: p.metrics.propertyName,
    value: p.metrics.sessions,
  }));

  return (
    <>
      <SectionTitle title="Overview" tooltip="Overall traffic across all product websites. Sessions = number of visits, Users = unique visitors, New Users = first-time visitors, Page Views = number of page displays." />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Total Sessions" value={report.totals.sessions.toLocaleString()} />
        <KpiCard label="Total Users" value={report.totals.totalUsers.toLocaleString()} />
        <KpiCard label="New Users" value={report.totals.newUsers.toLocaleString()} />
        <KpiCard label="Total Page Views" value={report.totals.pageViews.toLocaleString()} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
          <ChartTitle title="Sessions by Product (Daily)" tooltip="Daily session count per product website. Useful for spotting trends and traffic spikes." />
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={combinedDaily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              {report.properties.map((p) => (
                <Line
                  key={p.metrics.propertyName}
                  type="monotone"
                  dataKey={p.metrics.propertyName}
                  stroke={getProductColor(p.metrics.propertyName)}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <ChartTitle title="Sessions Distribution" tooltip="How sessions are split across products. Shows which site attracts the most visitors relative to the others." />
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                dataKey="value"
                label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={getProductColor(entry.name)} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}

// ===== Acquisition Section =====

function AcquisitionSection({ report }: { report: WebAnalyticsReport }) {
  return (
    <>
      <SectionTitle title="Acquisition" tooltip="Where visitors come from. Channels show the type of source (search engines, direct, social), Sources show the specific sites and platforms." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Channels */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <ChartTitle title="Channels" tooltip="Traffic channels: Organic Search = from search engines, Direct = typed or bookmarked URL, Referral = link from another site, Organic Social = from social networks." />
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={report.channels} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="channel" width={120} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="sessions" radius={[0, 4, 4, 0]}>
                {report.channels.map((entry) => (
                  <Cell key={entry.channel} fill={CHANNEL_COLORS[entry.channel] ?? '#9CA3AF'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Channel distribution pie */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <ChartTitle title="Channel Distribution" tooltip="Percentage split of sessions by channel. Helps identify which marketing channels are most effective." />
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={report.channels}
                cx="50%"
                cy="50%"
                outerRadius={100}
                dataKey="sessions"
                nameKey="channel"
                label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
              >
                {report.channels.map((entry) => (
                  <Cell key={entry.channel} fill={CHANNEL_COLORS[entry.channel] ?? '#9CA3AF'} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sources table */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <ChartTitle title="Top Sources / Medium" tooltip="Specific traffic sources. Source = where from (google, linkedin, direct), Medium = how (organic = unpaid search, cpc = paid ads, referral = inbound link)." />
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-4 font-medium text-gray-600">Source</th>
                <th className="text-left py-2 px-4 font-medium text-gray-600">Medium</th>
                <th className="text-right py-2 px-4 font-medium text-gray-600">Sessions</th>
                <th className="text-right py-2 pl-4 font-medium text-gray-600">Users</th>
              </tr>
            </thead>
            <tbody>
              {aggregateSources(report.properties)
                .slice(0, 15)
                .map((s, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900">{s.source}</td>
                    <td className="py-2 px-4 text-gray-500">{s.medium}</td>
                    <td className="py-2 px-4 text-right text-gray-700">{s.sessions.toLocaleString()}</td>
                    <td className="py-2 pl-4 text-right text-gray-500">{s.users.toLocaleString()}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function aggregateSources(properties: PropertyAnalytics[]) {
  const map = new Map<string, { source: string; medium: string; sessions: number; users: number }>();
  for (const p of properties) {
    for (const s of p.sources) {
      const key = `${s.source}|${s.medium}`;
      const existing = map.get(key);
      if (existing) {
        existing.sessions += s.sessions;
        existing.users += s.users;
      } else {
        map.set(key, { ...s });
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => b.sessions - a.sessions);
}

// ===== Audience Section (Geo + Devices) =====

function AudienceSection({
  countries,
  devices,
}: {
  countries: CountryBreakdown[];
  devices: DeviceBreakdown[];
}) {
  return (
    <>
      <SectionTitle title="Audience" tooltip="Who the visitors are — which countries they come from and which devices they use. Relevant for content targeting and site optimisation." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Countries table */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
          <ChartTitle title="Top Countries" tooltip="Countries visitors come from. Share is that country's portion of total traffic. Useful for B2B targeting and localisation." />
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">#</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">Country</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-600">Sessions</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-600">Users</th>
                  <th className="text-right py-2 pl-4 font-medium text-gray-600">Share</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const totalSessions = countries.reduce((sum, cc) => sum + cc.sessions, 0);
                  return countries.slice(0, 10).map((c, i) => {
                  const share = totalSessions > 0 ? (c.sessions / totalSessions) * 100 : 0;
                  return (
                    <tr key={c.country} className="border-b border-gray-100">
                      <td className="py-2 pr-4 text-gray-400">{i + 1}</td>
                      <td className="py-2 pr-4 text-gray-900 font-medium">{c.country}</td>
                      <td className="py-2 px-4 text-right text-gray-700">{c.sessions.toLocaleString()}</td>
                      <td className="py-2 px-4 text-right text-gray-500">{c.users.toLocaleString()}</td>
                      <td className="py-2 pl-4 text-right text-gray-500">{share.toFixed(1)}%</td>
                    </tr>
                  );
                });
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Devices pie */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <ChartTitle title="Devices" tooltip="Session split by device type. A mobile-dominant split means the mobile experience needs to be solid." />
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={devices}
                cx="50%"
                cy="50%"
                outerRadius={90}
                dataKey="sessions"
                nameKey="device"
                label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
              >
                {devices.map((entry) => (
                  <Cell key={entry.device} fill={DEVICE_COLORS[entry.device] ?? '#9CA3AF'} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>

          {/* Device stats */}
          <div className="mt-4 space-y-2">
            {devices.map((d) => (
              <div key={d.device} className="flex justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full inline-block"
                    style={{ backgroundColor: DEVICE_COLORS[d.device] ?? '#9CA3AF' }}
                  />
                  {d.device}
                </span>
                <span className="text-gray-600">
                  {d.sessions.toLocaleString()} sessions / {d.users.toLocaleString()} users
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ===== Engagement Section (per product) =====

const ENGAGEMENT_SOURCES = [
  { key: null, label: 'All' },
  { key: 'direct', label: 'Direct' },
  { key: 'google', label: 'Google' },
  { key: 'linkedin', label: 'LinkedIn' },
] as const;

function EngagementSection({
  report,
  from,
  to,
  selectedProduct,
}: {
  report: WebAnalyticsReport;
  from: string;
  to: string;
  selectedProduct: string | null;
}) {
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const { data: sourceData, isLoading: sourceLoading } = useEngagementBySource(
    from,
    to,
    sourceFilter
  );

  // Use source-filtered data when a source is selected, otherwise use report data
  const engagementData = sourceFilter && sourceData
    ? sourceData.map((d) => ({
        name: d.product,
        bounceRate: d.bounceRate * 100,
        avgDuration: d.avgSessionDuration,
        pagesPerSession: d.pagesPerSession,
        sessions: d.sessions,
      }))
    : report.properties.map((p) => ({
        name: p.metrics.propertyName,
        bounceRate: p.metrics.bounceRate * 100,
        avgDuration: p.metrics.avgSessionDuration,
        pagesPerSession:
          p.metrics.sessions > 0 ? p.metrics.pageViews / p.metrics.sessions : 0,
        sessions: p.metrics.sessions,
      }));

  return (
    <>
      <SectionTitle title="Engagement" tooltip="How visitors behave on the site. Bounce Rate = left without interacting (lower is better), Avg. Duration = average visit length, Pages/Session = how many pages they view. Filter by source to compare behaviour across channels." />

      {/* Source filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500 mr-1">Source:</span>
        {ENGAGEMENT_SOURCES.map((s) => {
          const isActive = sourceFilter === s.key;
          return (
            <button
              key={s.key ?? 'all'}
              onClick={() => setSourceFilter(s.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                isActive
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
              }`}
            >
              {s.label}
            </button>
          );
        })}
        {sourceFilter && sourceLoading && (
          <span className="text-xs text-gray-400 ml-2">Loading...</span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bounce Rate comparison */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <ChartTitle title="Bounce Rate by Product" tooltip="Share of visitors who left without any interaction. A high bounce rate can mean the content does not match expectations or the UX is poor." />
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={engagementData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="%" />
              <Tooltip formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Bounce Rate']} />
              <Bar dataKey="bounceRate" radius={[4, 4, 0, 0]}>
                {engagementData.map((entry) => (
                  <Cell key={entry.name} fill={getProductColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Avg Session Duration */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <ChartTitle title="Avg. Session Duration" tooltip="Average time a visitor spends on the site. Longer usually means more interest in the content and the product." />
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={engagementData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="s" />
              <Tooltip formatter={(value) => [formatDuration(Number(value)), 'Duration']} />
              <Bar dataKey="avgDuration" radius={[4, 4, 0, 0]}>
                {engagementData.map((entry) => (
                  <Cell key={entry.name} fill={getProductColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pages per Session */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <ChartTitle title="Pages per Session" tooltip="Average number of pages viewed per visit. A higher number means visitors explore the site in depth (pricing, about, security)." />
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={engagementData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value) => [Number(value).toFixed(2), 'Pages/Session']} />
              <Bar dataKey="pagesPerSession" radius={[4, 4, 0, 0]}>
                {engagementData.map((entry) => (
                  <Cell key={entry.name} fill={getProductColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Trend */}
      <EngagementTrendCharts selectedProduct={selectedProduct} />
    </>
  );
}

function EngagementTrendCharts({ selectedProduct }: { selectedProduct: string | null }) {
  const { data: trendData, isLoading } = useEngagementTrend(true, 6);

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <span className="text-sm text-gray-400">Loading monthly trend...</span>
      </div>
    );
  }

  if (!trendData || trendData.length === 0) return null;

  // Filter by selected product
  const filteredData = selectedProduct
    ? trendData.filter((d) => d.product === selectedProduct)
    : trendData;

  // Pivot data: { month, PriceMonk_bounceRate, MatchMonk_bounceRate, ... }
  const months = [...new Set(filteredData.map((d) => d.month))].sort();
  const products = [...new Set(filteredData.map((d) => d.product))];

  const bounceData = months.map((month) => {
    const row: Record<string, string | number> = { month };
    for (const product of products) {
      const entry = filteredData.find((d) => d.month === month && d.product === product);
      row[product] = entry ? Math.round(entry.bounceRate * 100) : 0;
    }
    return row;
  });

  const durationData = months.map((month) => {
    const row: Record<string, string | number> = { month };
    for (const product of products) {
      const entry = filteredData.find((d) => d.month === month && d.product === product);
      row[product] = entry ? Math.round(entry.avgSessionDuration) : 0;
    }
    return row;
  });

  const pagesData = months.map((month) => {
    const row: Record<string, string | number> = { month };
    for (const product of products) {
      const entry = filteredData.find((d) => d.month === month && d.product === product);
      row[product] = entry ? Math.round(entry.pagesPerSession * 100) / 100 : 0;
    }
    return row;
  });

  return (
    <>
      <ChartTitle title="Monthly Trend (last 6 months)" tooltip="Engagement metrics by month. Watch for Bounce Rate going down (better), Avg. Duration going up (better) and Pages/Session going up (better). Comparing products shows which site is improving fastest." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Bounce Rate trend <span className="text-xs text-gray-400">(lower is better)</span></h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={bounceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="%" />
              <Tooltip formatter={(value) => [`${value}%`, '']} />
              <Legend />
              {products.map((p) => (
                <Line key={p} type="monotone" dataKey={p} stroke={getProductColor(p)} strokeWidth={2} dot />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Avg. Duration trend <span className="text-xs text-gray-400">(higher is better)</span></h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={durationData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="s" />
              <Tooltip formatter={(value) => [formatDuration(Number(value)), '']} />
              <Legend />
              {products.map((p) => (
                <Line key={p} type="monotone" dataKey={p} stroke={getProductColor(p)} strokeWidth={2} dot />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Pages/Session trend <span className="text-xs text-gray-400">(higher is better)</span></h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={pagesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              {products.map((p) => (
                <Line key={p} type="monotone" dataKey={p} stroke={getProductColor(p)} strokeWidth={2} dot />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}

// ===== Conversions Section =====

const CONVERSION_EVENTS = [
  'contact_modal_open',
  'contact_form_submit',
  'contact_form_success',
  'cta_click',
  'video_play',
];

const EVENT_LABELS: Record<string, string> = {
  contact_modal_open: 'Contact Modal Opened',
  contact_form_submit: 'Form Submitted',
  contact_form_success: 'Form Success',
  cta_click: 'CTA Clicked',
  video_play: 'Video Played',
};

const EVENT_COLORS: Record<string, string> = {
  contact_modal_open: '#3B82F6',
  contact_form_submit: '#FC8332',
  contact_form_success: '#10B981',
  cta_click: '#8B5CF6',
  video_play: '#F59E0B',
};

function ConversionsSection({ report }: { report: WebAnalyticsReport }) {
  // Filter only our custom conversion events
  const conversionEvents = report.events.filter((e) =>
    CONVERSION_EVENTS.includes(e.eventName)
  );

  // Per-product conversion events
  const productConversions = report.properties.map((p) => {
    const events = p.events.filter((e) => CONVERSION_EVENTS.includes(e.eventName));
    const modalOpens = events.find((e) => e.eventName === 'contact_modal_open')?.count ?? 0;
    const formSubmits = events.find((e) => e.eventName === 'contact_form_submit')?.count ?? 0;
    const conversionRate =
      p.metrics.sessions > 0 ? (modalOpens / p.metrics.sessions) * 100 : 0;
    const formRate = modalOpens > 0 ? (formSubmits / modalOpens) * 100 : 0;

    return {
      name: p.metrics.propertyName,
      modalOpens,
      formSubmits,
      conversionRate,
      formRate,
      events,
    };
  });

  const hasData = conversionEvents.length > 0;

  return (
    <>
      <SectionTitle title="Conversions" tooltip="Conversions — how many visitors completed a desired action (opening the contact form, submitting it, clicking a CTA, playing a video). Visit → Modal = share of visitors who opened the form, Modal → Submit = share of those who submitted it." />

      {!hasData ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-700 text-sm">
          No conversion events detected yet. Custom GA4 events (contact_modal_open,
          contact_form_submit, cta_click, video_play) have been added to the product
          websites. Data will appear here once the updated code is deployed and users
          start interacting with the sites.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conversion funnel */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <ChartTitle title="Conversion Events" tooltip="Count of each conversion action on the sites: opening the form, submitting it, clicking a CTA button, and playing the product video." />
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={conversionEvents} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="eventName"
                  width={150}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(name: string) => EVENT_LABELS[name] ?? name}
                />
                <Tooltip
                  labelFormatter={(name) => EVENT_LABELS[String(name)] ?? String(name)}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {conversionEvents.map((entry) => (
                    <Cell
                      key={entry.eventName}
                      fill={EVENT_COLORS[entry.eventName] ?? '#9CA3AF'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Conversion rates per product */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <ChartTitle title="Conversion Rates by Product" tooltip="Conversion ratios per product. Visit → Modal = percentage of visits that opened the contact form. Modal → Submit = percentage of opened forms that were submitted." />
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 pr-4 font-medium text-gray-600">Product</th>
                    <th className="text-right py-2 px-4 font-medium text-gray-600">Modal Opens</th>
                    <th className="text-right py-2 px-4 font-medium text-gray-600">Submits</th>
                    <th className="text-right py-2 px-4 font-medium text-gray-600">Visit → Modal</th>
                    <th className="text-right py-2 pl-4 font-medium text-gray-600">Modal → Submit</th>
                  </tr>
                </thead>
                <tbody>
                  {productConversions.map((p) => (
                    <tr key={p.name} className="border-b border-gray-100">
                      <td className="py-2 pr-4 font-medium" style={{ color: PRODUCT_COLORS[p.name] }}>
                        {p.name}
                      </td>
                      <td className="py-2 px-4 text-right text-gray-700">{p.modalOpens}</td>
                      <td className="py-2 px-4 text-right text-gray-700">{p.formSubmits}</td>
                      <td className="py-2 px-4 text-right text-gray-500">{p.conversionRate.toFixed(1)}%</td>
                      <td className="py-2 pl-4 text-right text-gray-500">{p.formRate.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ===== Product Detail Section =====

function ProductDetailSection({ data }: { data: PropertyAnalytics }) {
  const { metrics, sources, channels, daily, topPages } = data;
  const color = getProductColor(metrics.propertyName);
  const chartDaily = daily.map((d) => ({ ...d, date: formatDate(d.date) }));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
      <h3 className="text-lg font-semibold" style={{ color }}>
        {metrics.propertyName}
      </h3>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KpiCard label="Sessions" value={metrics.sessions.toLocaleString()} />
        <KpiCard label="Users" value={metrics.totalUsers.toLocaleString()} />
        <KpiCard label="New Users" value={metrics.newUsers.toLocaleString()} />
        <KpiCard label="Page Views" value={metrics.pageViews.toLocaleString()} />
        <KpiCard label="Avg. Duration" value={formatDuration(metrics.avgSessionDuration)} />
        <KpiCard label="Bounce Rate" value={`${(metrics.bounceRate * 100).toFixed(1)}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily traffic chart */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Daily Traffic</h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartDaily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sessions" stroke={color} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="users" stroke="#9CA3AF" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Channels */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Channels</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={channels} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="channel" width={120} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="sessions" fill={color} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sources table */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Traffic Sources</h4>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-4 font-medium text-gray-600">Source / Medium</th>
                <th className="text-right py-2 px-4 font-medium text-gray-600">Sessions</th>
                <th className="text-right py-2 pl-4 font-medium text-gray-600">Users</th>
              </tr>
            </thead>
            <tbody>
              {sources.slice(0, 10).map((s, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2 pr-4 text-gray-900">
                    {s.source} <span className="text-gray-400">/ {s.medium}</span>
                  </td>
                  <td className="py-2 px-4 text-right text-gray-700">{s.sessions.toLocaleString()}</td>
                  <td className="py-2 pl-4 text-right text-gray-500">{s.users.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Top pages table */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Top Pages</h4>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-4 font-medium text-gray-600">Page</th>
                <th className="text-right py-2 px-4 font-medium text-gray-600">Views</th>
                <th className="text-right py-2 pl-4 font-medium text-gray-600">Avg. Time</th>
              </tr>
            </thead>
            <tbody>
              {topPages.map((page) => (
                <tr key={page.path} className="border-b border-gray-100">
                  <td className="py-2 pr-4 text-gray-900 font-mono text-xs">{page.path}</td>
                  <td className="py-2 px-4 text-right text-gray-700">{page.pageViews.toLocaleString()}</td>
                  <td className="py-2 pl-4 text-right text-gray-500">{formatDuration(page.avgTimeOnPage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===== Diagnostics Banners =====
//
// Both banners are derived at request time from the report's `diagnostics`
// block and carry no configuration surface (D-04): no close/mute control and
// no client-side suppression of any kind. They disappear on their own once
// the underlying arrays are empty.

function StreamWarningBanner({ warnings }: { warnings: StreamWarning[] }) {
  if (warnings.length === 0) return null;

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-orange-800">
      <p className="font-medium">Stream configuration warning</p>
      <p className="text-sm mt-1">
        A configured GA4 stream is receiving no data for the site(s) listed below, even
        though those hostnames do have traffic. Their numbers are being reported without
        a stream filter, so their pageviews may be inflated. An admin should correct the
        canonical stream on the{' '}
        <Link to="/admin/web-analytics-sites" className="underline font-medium">
          site registry
        </Link>
        .
      </p>
      <ul className="mt-2 text-sm space-y-1">
        {warnings.map((w) => (
          <li key={w.hostname}>
            <span className="font-medium">{w.hostname}</span> ({w.productName}) —
            configured stream <span className="font-mono">{w.canonicalStreamId}</span>,
            observed streams: {w.observedStreamIds.join(', ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DuplicateTagBanner({ sites }: { sites: DuplicateTagSite[] }) {
  if (sites.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
      <p className="font-medium">Duplicate GA4 tags detected</p>
      <p className="text-sm mt-1">
        More than one GA4 stream is recording pageviews for the site(s) listed below, so
        their pageview counts are inflated. Sessions and users are only lightly affected
        (roughly 1.15x) and remain reliable — only pageviews are significantly inflated
        (roughly 4x). The fix is on the website side. See the{' '}
        <Link to="/admin/web-analytics-sites" className="underline font-medium">
          site registry
        </Link>{' '}
        for details.
      </p>
      <ul className="mt-2 text-sm space-y-1">
        {sites.map((s) => (
          <li key={s.hostname}>
            <span className="font-medium">{s.hostname}</span> ({s.productName}) —
            streams: {s.streamIds.join(', ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ===== Main Page =====

export default function WebAnalyticsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<WebAnalyticsFilters | null>(() => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      from: `${firstOfMonth.getFullYear()}-${pad(firstOfMonth.getMonth() + 1)}-${pad(firstOfMonth.getDate())}`,
      to: `${lastOfMonth.getFullYear()}-${pad(lastOfMonth.getMonth() + 1)}-${pad(lastOfMonth.getDate())}`,
    };
  });
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const { data: ga4Properties } = useGA4Properties();
  const productList = ga4Properties?.map((p) => p.name) ?? [];
  const { data: report, isLoading, error } = useWebAnalyticsReport(
    filters
      ? { ...filters, products: selectedProduct ? [selectedProduct] : undefined }
      : null
  );

  // If the registry drops the previously selected product (an admin
  // deactivated its last hostname), clear the selection so the page
  // cannot stay stuck sending a `?products=` value the backend now
  // rejects with a 400 (T-85-33).
  useEffect(() => {
    if (!ga4Properties) return;
    if (selectedProduct && !ga4Properties.some((p) => p.name === selectedProduct)) {
      setSelectedProduct(null);
    }
  }, [ga4Properties, selectedProduct]);

  const toggleProduct = (product: string) => {
    setSelectedProduct((prev) => (prev === product ? null : product));
  };

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/reports')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Reports
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Web Analytics</h1>
        <p className="text-sm text-gray-500 mt-1">
          Traffic data from product websites
        </p>
      </div>

      <ReportFilters onApply={(f) => setFilters(f)} isLoading={isLoading} />

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500 mr-1">Product:</span>
        <button
          onClick={() => setSelectedProduct(null)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
            selectedProduct === null
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
          }`}
        >
          All
        </button>
        {productList.map((product) => {
          const isSelected = selectedProduct === product;
          const color = getProductColor(product);
          return (
            <button
              key={product}
              onClick={() => toggleProduct(product)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                isSelected
                  ? 'text-white'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
              }`}
              style={
                isSelected
                  ? { backgroundColor: color, borderColor: color }
                  : undefined
              }
            >
              {product}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error.message}
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {report && (
        <>
          <StreamWarningBanner warnings={report.diagnostics.streamWarnings} />
          <DuplicateTagBanner sites={report.diagnostics.duplicateTagSites} />

          <OverviewSection report={report} />
          <AcquisitionSection report={report} />
          <AudienceSection countries={report.countries} devices={report.devices} />
          <EngagementSection report={report} from={filters!.from} to={filters!.to} selectedProduct={selectedProduct} />
          <ConversionsSection report={report} />

          {/* Per-product detail sections */}
          <h2 className="text-lg font-semibold text-gray-900 mt-8">Product Details</h2>
          {report.properties.map((prop) => (
            <ProductDetailSection key={prop.metrics.propertyId + prop.metrics.propertyName} data={prop} />
          ))}
        </>
      )}
    </div>
  );
}
